import { defineStore } from 'pinia';
import { computed } from 'vue';
import { useContentRefData } from '@ilteducation/content-ref-data';
import { useTokens } from '@ilteducation/ilt-auth';
import type { Language } from '@ilteducation/content-ref-data-types/models/data-types';
import config from '../config/ConfigProvider';

const useLanguagesStore = defineStore('languages', () => {
  const refDataStore = useContentRefData();
  const tokensStore = useTokens();

  // content-ref-data is behind auth, so this can only be loaded for a visitor with a session.
  // Most consumers of this store only want getMarketLanguage(), which is derived from config, and
  // on the public video page there is no token at all - the auth interceptor would cancel the
  // request and log a failure for data that page never reads.
  tokensStore
    .getValidTokens()
    .then((tokens) => (tokens ? refDataStore.references('languages') : undefined))
    .catch(() => {
      // getValidTokens rejects when a refresh fails, which it handles by logging the visitor out.
    });

  const languages = computed(() => {
    const refDataLangs = [...(refDataStore.all('languages') as Language[])];
    // Ukrainian is called 'uk' in ref data, but we use 'ukr' in Babelfish/Lokalise so we add a duplicate entry
    const ukrainian = refDataLangs.find((lang) => lang.languageCode === 'uk');
    if (ukrainian) {
      refDataLangs.push({
        ...ukrainian,
        languageCode: 'ukr',
      });
    }
    // Turkish has language code 'tk' in ref data, 'tr' is used in Babelfish/Lokalise, so we add a duplicate entry
    const turkish = refDataLangs.find((lang) => lang.languageCode === 'tk');
    if (turkish) {
      refDataLangs.push({
        ...turkish,
        languageCode: 'tr',
      });
    }
    return refDataLangs.sort((a, b) => a.language.localeCompare(b.language));
  });
  const supportedAreaGuideContentLanguages = [
    'ar',
    'de',
    'en-gb',
    'es',
    'prs',
    'fr',
    'ru',
    'sv',
    'ukr',
    'ur',
    'so',
    'ti',
    'fi',
    'tr',
  ];

  const getLanguageByShortcode = (code: string) => languages.value.find((lang) => lang.languageCode === code);

  const getMarketLanguage = () => {
    const { countryCode } = config.CONFIG;

    switch (countryCode) {
      case 'no':
        return 'nb';
      case 'fi':
        return 'fi';
      default:
        return 'sv';
    }
  };

  return {
    languages,
    getLanguageByShortcode,
    getMarketLanguage,
    supportedAreaGuideContentLanguages,
  };
});

export default useLanguagesStore;
